import { FilterType } from '@/types/filters'
import { useSalespointStore } from '@/modules/settings/salespoints/stores/salespoint.store'
import { ProductSetting } from '@/modules/products/types/product-setting.types'
import { ProductType, ProductOptionType, ProductOptionKeyType } from '@/modules/products/types/product.types'
import { COUPON_TYPE_OPTIONS } from '@/modules/coupons/types/coupons.types'

/**
 * Status polling interval in milliseconds
 * Uses environment variable VITE_STATUS_POLLING_INTERVAL_MS with 30 seconds default
 */
export const STATUS_POLLING_INTERVAL_MS = parseInt(import.meta.env.VITE_STATUS_POLLING_INTERVAL_MS || '30000', 10)

/**
 * Enum for valid printer lookup keys
 */
export enum PRINTER_KEYS {
  SERIAL_NUMBER = 'serial_number',
  ID = 'id',
  REFERENCE_XID = 'reference_xid'
}

export enum BUSINESS_SETTINGS {
  ABBREVIATION = 'business_abbreviation',
  ADDRESS = 'business_address',
  ALLOWED_DOMAINS = 'allowed_domains',
  CUSTOMER_SUPPORT_EMAIL = 'customer_support_email',
  CUSTOMER_SUPPORT_PHONE = 'customer_support_phone',
  CUSTOMER_SUPPORT_URL = 'customer_support_url',
  DOMAIN = 'domain_name',
  FERRY_SCHEDULE_URL = 'ferry_schedule_url',
  LOGO_URL = 'logo_url',
  NAME = 'business_name',
  PRIVACY_POLICY_URL = 'privacy_policy_url',
  TERMS_OF_SERVICE_URL = 'terms_of_service_url'
}

export enum SYSTEM_SETTINGS {
  ALLOWED_DOMAINS = 'allowed_domains',
  HOTJAR_SITE_ID = 'hotjar_site_id',
  POS_INACTIVE_TIMEOUT = 'pos_inactive_timeout'
}

export enum ONLINE_SALES_SETTINGS {
  TICKETS_BUNDLING_METHOD = 'tickets_bundling_method'
}

export enum POS_SETTINGS {
  RECENT_ORDERS_OPEN_NEW_TAB = 'recent_orders_open_new_tab',
  REQUIRE_NOTES_FOR_COMPLIMENTARY_ITEMS = 'require_notes_for_complimentary_items'
}

export enum UserRole {
  SYSTEM = 'system',
  ADMIN = 'admin',
  ANALYST = 'analyst',
  SALES = 'sales',
  SUPERVISOR = 'supervisor',
  ACCESS_CONTROL = 'access_control',
  ALL = 'all'
}

export enum ORDER_STATUSES {
  PENDING = 'Pending',
  INCOMPLETE = 'Incomplete',
  UNCAPTURED = 'Uncaptured',
  CAPTURED = 'Captured',
  COMPLETED = 'Completed',
  REFUNDED = 'Refunded',
  CANCELLED = 'Cancelled',
  PARTIALLY_REFUNDED = 'Partially Refunded'
}

export enum ORDER_ITEM_PASS_TYPES {
  MOBILE_PASS = 'mobile',
  PRINT_PASS = 'print',
  CARD_PASS = 'card'
}

export enum TransactionType {
  POS = 'pos',
  WEB = 'web'
}

export enum SalespointFilterFields {
  ASSIGNED = 'assigned',
  UNASSIGNED = 'unassigned',
  LOCATION = 'location_id'
}

export enum PaymentStatus {
  COMPLETED = 'completed',
  PROCESSING = 'processing',
  FAILED = 'failed',
  CANCELLED = 'cancelled'
}

export const ACTION_ROLES = {
  CARD_PASS_CREATE: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES],
  CARD_PASS_UPDATE: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES],
  CARD_PASS_DEACTIVATE: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES],
  CARD_PASS_ADD_SCAN: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR],
  CARD_PASS_REPLACE: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES],
  CARD_PASS_VOID: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES],
  CARD_PASS_FREEZE: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES],
  CARD_PASS_ASSIGN_CUSTOMER: [UserRole.SYSTEM, UserRole.ADMIN],
  CARD_PASS_DETAILS_VIEW: [
    UserRole.SYSTEM,
    UserRole.ADMIN,
    UserRole.SUPERVISOR,
    UserRole.SALES,
    UserRole.ANALYST,
    UserRole.ACCESS_CONTROL
  ],
  CARD_PASSES_IMPORT: [UserRole.SYSTEM, UserRole.ADMIN],
  CARD_PASSES_EXPORT: [
    UserRole.SYSTEM,
    UserRole.ADMIN,
    UserRole.SUPERVISOR,
    UserRole.SALES,
    UserRole.ANALYST,
    UserRole.ACCESS_CONTROL
  ],
  CARD_PASSES_VIEW: [
    UserRole.SYSTEM,
    UserRole.ADMIN,
    UserRole.SUPERVISOR,
    UserRole.SALES,
    UserRole.ANALYST,
    UserRole.ACCESS_CONTROL
  ],
  PRINTED_TICKETS_VIEW: [
    UserRole.SYSTEM,
    UserRole.ADMIN,
    UserRole.SUPERVISOR,
    UserRole.SALES,
    UserRole.ANALYST,
    UserRole.ACCESS_CONTROL
  ],
  PRINTED_TICKETS_EXPORT: [
    UserRole.SYSTEM,
    UserRole.ADMIN,
    UserRole.SUPERVISOR,
    UserRole.SALES,
    UserRole.ANALYST,
    UserRole.ACCESS_CONTROL
  ],
  COUPONS_VIEW: [UserRole.SYSTEM],
  COUPONS_CREATE: [UserRole.SYSTEM],
  COUPONS_UPDATE: [UserRole.SYSTEM],
  COUPONS_DELETE: [UserRole.SYSTEM],
  COUPONS_ACTIVATE: [UserRole.SYSTEM],
  COUPONS_DEACTIVATE: [UserRole.SYSTEM],
  COUPONS_EXPORT: [UserRole.SYSTEM],

  PRINTED_TICKET_DETAILS_VIEW: [
    UserRole.SYSTEM,
    UserRole.ADMIN,
    UserRole.SUPERVISOR,
    UserRole.SALES,
    UserRole.ANALYST,
    UserRole.ACCESS_CONTROL
  ],

  PRINTED_TICKET_VOID: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES],
  PRINTED_TICKET_ADD_SCAN: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR],
  CUSTOMER_DETAILS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES, UserRole.ANALYST],
  CUSTOMER_PASSWORD_RESET: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES],
  CUSTOMER_UPDATE: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES],
  CUSTOMERS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES, UserRole.ANALYST],
  CUSTOMERS_EXPORT: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES, UserRole.ANALYST],
  DECRYPT_VIEW: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES, UserRole.ANALYST],
  PASS_DETAILS_VIEW: [
    UserRole.SYSTEM,
    UserRole.ADMIN,
    UserRole.SUPERVISOR,
    UserRole.SALES,
    UserRole.ANALYST,
    UserRole.ACCESS_CONTROL
  ],
  MOBILE_PASS_REGISTRATIONS_VIEW: [
    UserRole.SYSTEM,
    UserRole.ADMIN,
    UserRole.SUPERVISOR,
    UserRole.SALES,
    UserRole.ANALYST
  ],
  MOBILE_PASS_VIEW: [
    UserRole.SYSTEM,
    UserRole.ADMIN,
    UserRole.SUPERVISOR,
    UserRole.SALES,
    UserRole.ANALYST,
    UserRole.ACCESS_CONTROL
  ],
  MOBILE_PASS_EXPORT: [
    UserRole.SYSTEM,
    UserRole.ADMIN,
    UserRole.SUPERVISOR,
    UserRole.SALES,
    UserRole.ANALYST,
    UserRole.ACCESS_CONTROL
  ],
  MOBILE_PASS_RESEND: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES],
  PASS_ADD_SCAN: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR],
  PASS_VOID: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES],
  NOTE_CREATE: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES],
  NOTE_UPDATE: [UserRole.SYSTEM, UserRole.ADMIN],
  NOTES_VIEW: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES, UserRole.ANALYST],
  ORDER_DETAILS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES, UserRole.ANALYST],
  ORDER_CART_DETAILS_VIEW: [UserRole.SYSTEM],
  ORDER_EMAIL_HISTORY: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES, UserRole.ANALYST],
  ORDERS_EXPORT: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES, UserRole.ANALYST],
  ORDER_PASSES_PRINT: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES],
  ORDER_REFUND: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES],
  ORDER_REFUND_RESEND: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES],
  ORDER_RESEND: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES],
  ORDERS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES, UserRole.ANALYST],
  ORDER_VIEW_HOTJAR_RECORDING: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR],
  ORDER_VIEW_POS_INFORMATION: [UserRole.SYSTEM],
  ORDER_ACTIONS_MENU: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES, UserRole.ANALYST],
  PAYMENTS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES, UserRole.ANALYST],
  PAYMENT_ACTIONS_MENU: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES, UserRole.ANALYST],
  PAYMENT_DETAILS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES, UserRole.ANALYST],
  PAYMENT_REFUND: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES],
  PAYMENTS_EXPORT: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES, UserRole.ANALYST],
  PAYMENTS_VIEW_PSP_ID: [UserRole.SYSTEM],
  PASS_UPDATE_EXPIRATION_DATE: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR],
  PRINT_PASSES: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES],
  PAYLOADS_DECRYPT: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES, UserRole.ANALYST],
  QR_CODES_VIEW: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.ANALYST],
  QR_CODE_EDIT: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR],
  QR_CODE_ENABLE: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR],
  QR_CODE_DISABLE: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR],
  QR_CODES_EXPORT: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.ANALYST],
  REFUNDS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES, UserRole.ANALYST],
  REFUNDS_EXPORT: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES, UserRole.ANALYST],
  REPORTS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.ANALYST],
  REVERSED_SCANS_VIEW: [
    UserRole.SYSTEM,
    UserRole.ADMIN,
    UserRole.SUPERVISOR,
    UserRole.SALES,
    UserRole.ANALYST,
    UserRole.ACCESS_CONTROL
  ],
  REVERSED_SCANS_EXPORT: [
    UserRole.SYSTEM,
    UserRole.ADMIN,
    UserRole.SUPERVISOR,
    UserRole.SALES,
    UserRole.ANALYST,
    UserRole.ACCESS_CONTROL
  ],
  SCANS_VIEW: [
    UserRole.SYSTEM,
    UserRole.ADMIN,
    UserRole.SUPERVISOR,
    UserRole.SALES,
    UserRole.ANALYST,
    UserRole.ACCESS_CONTROL
  ],
  SCANS_EXPORT: [
    UserRole.SYSTEM,
    UserRole.ADMIN,
    UserRole.SUPERVISOR,
    UserRole.SALES,
    UserRole.ANALYST,
    UserRole.ACCESS_CONTROL
  ],
  SCAN_REVERSE: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR],
  SETTINGS_CREATE: [UserRole.SYSTEM, UserRole.ADMIN],
  SETTINGS_UPDATE: [UserRole.SYSTEM, UserRole.ADMIN],
  SETTINGS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN],
  USER_CREATE: [UserRole.SYSTEM, UserRole.ADMIN],
  USER_PASSWORD_RESET: [UserRole.SYSTEM, UserRole.ADMIN],
  USER_ROLE_UPDATE: [UserRole.SYSTEM, UserRole.ADMIN],
  USER_UPDATE: [UserRole.SYSTEM, UserRole.ADMIN],
  USERS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR],
  USER_DETAILS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR],
  USER_SECURITY_VIEW: [UserRole.SYSTEM],
  USER_EXPORT: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR],
  SECURITY_VIEW: [
    UserRole.SYSTEM,
    UserRole.ADMIN,
    UserRole.SUPERVISOR,
    UserRole.ANALYST,
    UserRole.SALES,
    UserRole.ACCESS_CONTROL
  ],
  PAYMENT_TERMINALS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR],
  VESSELS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR],
  SIGN_OUT_SESSION: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR],
  SIGN_OUT_ALL_SESSIONS: [UserRole.SYSTEM, UserRole.ADMIN],
  SYSTEM_VIEW: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR],
  SETTINGS_BUSINESS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN],
  SETTINGS_LOCATIONS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN],
  SETTINGS_SALESPOINTS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN],
  SETTINGS_SALESPOINTS_EDIT: [UserRole.SYSTEM, UserRole.ADMIN],
  SETTINGS_PRINTERS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN],
  SETTINGS_PRINTER_TYPES_VIEW: [UserRole.SYSTEM, UserRole.ADMIN],
  SETTINGS_FARES_VIEW: [UserRole.SYSTEM, UserRole.ADMIN],
  SETTINGS_ROUTES_VIEW: [UserRole.SYSTEM, UserRole.ADMIN],
  SETTINGS_ZONES_VIEW: [UserRole.SYSTEM, UserRole.ADMIN],
  SETTINGS_ZONE_TYPES_VIEW: [UserRole.SYSTEM, UserRole.ADMIN],
  SETTINGS_PAYMENT_TERMINALS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN],
  SETTINGS_VESSELS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN],
  SETTINGS_SYSTEM_VIEW: [UserRole.SYSTEM],
  SETTINGS_ONLINE_SALES_VIEW: [UserRole.SYSTEM, UserRole.ADMIN],
  SETTINGS_POS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN],
  VALIDATORS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.ACCESS_CONTROL],
  VALIDATORS_EXPORT: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.ACCESS_CONTROL],
  VALIDATOR_DETAILS_VIEW: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.ACCESS_CONTROL],
  VALIDATOR_UPDATE: [UserRole.SYSTEM, UserRole.ADMIN],
  VALIDATOR_DEACTIVATE: [UserRole.SYSTEM, UserRole.ADMIN],
  SIGN_OUT: [UserRole.ALL],
  AUTO_SIGN_IN: [UserRole.SYSTEM],
  INVITE_ADD_AUTHENTICATOR: [
    UserRole.SYSTEM,
    UserRole.ADMIN,
    UserRole.SUPERVISOR,
    UserRole.SALES,
    UserRole.ANALYST,
    UserRole.ACCESS_CONTROL
  ],
  INVITE_ADD_PASSKEY: [
    UserRole.SYSTEM,
    UserRole.ADMIN,
    UserRole.SUPERVISOR,
    UserRole.SALES,
    UserRole.ANALYST,
    UserRole.ACCESS_CONTROL
  ],
  SIDEBAR_POS_TOP: [UserRole.SALES],
  SIDEBAR_PRONTO: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR],
  SIDEBAR_STRIPE: [UserRole.SYSTEM],
  SIDEBAR_POS: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR],

  /* --- EXPERIMENTAL --- */
  PRODUCTS_VIEW: [UserRole.SYSTEM],
  PRODUCT_DETAILS_VIEW: [UserRole.SYSTEM],
  PRODUCT_PUBLISH: [UserRole.SYSTEM],
  PRODUCT_EDIT: [UserRole.SYSTEM],
  PRODUCT_DELETE: [UserRole.SYSTEM],
  PRODUCT_CREATE: [UserRole.SYSTEM],
  POS_NEW_ORDER_VIEW: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES],
  ORDERS_TEST_PAGE_VIEW: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.ANALYST, UserRole.SALES],
  READ_A_CARD: [UserRole.SYSTEM, UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.SALES, UserRole.ANALYST]
}

export const AVAILABLE_CC_ICONS = ['visa', 'mastercard', 'amex', 'discover', 'diners', 'unionpay', 'jcb']

export const MAPPED_CC_NETWORKS: { [network: string]: string } = {
  amex: 'American Express',
  diners: 'Diners Club',
  unionpay: 'UnionPay',
  jcb: 'JCB'
}

const routeOptions = () => {
  const routeFilters = import.meta.env.VITE_ROUTE_FILTER_OPTIONS
  return (routeFilters && routeFilters.split(',')) || []
}

const fareOptions = () => {
  const fareFilters = import.meta.env.VITE_FARE_FILTER_OPTIONS
  return (fareFilters && fareFilters.split(',')) || []
}

export const salespointOptions = () => {
  const salespointStore = useSalespointStore()
  return salespointStore.getSalespointsOptions()
}

export const userOptions = async () => {
  const { useUserStore } = await import('@/modules/users/stores/user.store')
  const userStore = useUserStore()
  const response = await userStore.getUserOptions()
  return response.data.users.map((user: { name: string; id: string }) => ({
    label: user.name,
    value: user.id
  }))
}

export const couponOptions = async () => {
  const { getCouponsOptions } = await import('@/modules/coupons/api/coupons.api')
  return await getCouponsOptions()
}

export const ORDERS_LIST_FILTERS = [
  {
    name: 'date',
    label: 'Date',
    type: 'dateSelector',
    field: 'inserted_at'
  },
  {
    name: 'status',
    label: 'Status',
    type: 'checkboxList',
    field: 'status',
    options: [
      { label: 'Incomplete', value: 'Incomplete' },
      { label: 'Uncaptured', value: 'Uncaptured' },
      { label: 'Completed', value: 'Completed' },
      { label: 'Refunded', value: 'Refunded' },
      { label: 'Partially Refunded', value: 'Partially Refunded' },
      { label: 'Pending', value: 'Pending' },
      { label: 'Cancelled', value: 'Cancelled' }
    ]
  },
  {
    name: 'salespoint',
    label: 'Salespoint',
    type: 'selectWithSearch',
    multiple: true,
    field: 'salespoint',
    options: salespointOptions
  },
  {
    name: 'user',
    label: 'User',
    type: 'selectWithSearch',
    multiple: true,
    field: 'user',
    options: userOptions
  },
  {
    name: 'coupon_code',
    label: 'Coupon',
    type: 'selectWithSearch',
    multiple: true,
    field: 'coupon_code',
    options: couponOptions
  },
  {
    name: 'mediaType',
    label: 'Media type',
    type: 'checkboxList',
    field: 'media_type',
    options: [
      { label: 'Card', value: 'card' },
      { label: 'Mobile Pass', value: 'mobile_pass' },
      { label: 'Print', value: 'print' }
    ]
  },
  {
    name: 'route',
    label: 'Route',
    type: 'checkboxList',
    field: 'route',
    options: routeOptions().map((option: string) => ({ label: option, value: option }))
  },
  {
    name: 'fare',
    label: 'Fare',
    type: 'checkboxList',
    field: 'fare',
    options: fareOptions().map((option: string) => ({ label: option, value: option }))
  },
  { name: 'totalAmount', label: 'Total amount', type: 'numberRange', field: 'amount_total' },
  { name: 'discountAmount', label: 'Discount amount', type: 'numberRange', field: 'amount_discount' },
  { name: 'creditAmount', label: 'Credit amount', type: 'numberRange', field: 'amount_credit' },
  {
    name: 'creditTenderType',
    label: 'Credit tender type',
    type: 'checkboxList',
    field: 'credit_tender_type',
    options: [
      { label: 'Cash', value: 'cash' },
      { label: 'Check', value: 'check' },
      { label: 'Gift Certificate', value: 'gift_certificate' }
    ],
    allowOtherOption: true
  },
  { name: 'refundAmount', label: 'Refund amount', type: 'numberRange', field: 'amount_refund' },
  {
    name: 'refunds',
    label: 'Refunds',
    type: 'numberRange',
    field: 'refund_count',
    defaultOptions: { option: 'greater_than', first: 1 }
  },
  {
    name: 'complimentaryItems',
    label: 'Complimentary Items',
    type: 'dropdown',
    field: 'contains_complimentary_items',
    options: [
      { label: 'Yes', value: '1' },
      { label: 'No', value: '0' }
    ]
  },
  {
    name: 'productType',
    label: 'Product type',
    type: 'checkboxList',
    field: 'product_type',
    options: Object.values(ProductType)
      .sort((a, b) => a.localeCompare(b))
      .map((type) => {
        // Doing this manually because titleCase is not working due to circular dependency
        const label = type.charAt(0).toUpperCase() + type.slice(1)
        return { label, value: type }
      })
  },
  {
    name: 'transactionType',
    label: 'Transaction type',
    type: 'checkboxList',
    field: 'transaction_type',
    options: [
      { label: 'Web', value: 'web' },
      { label: 'Terminal', value: 'terminal' }
    ]
  },
  {
    name: 'tenderType',
    label: 'Tender type',
    type: 'checkboxList',
    field: 'tender_type',
    options: [
      { label: 'Credit Card', value: 'credit' },
      { label: 'Debit Card', value: 'debit' },
      { label: 'Cash', value: 'cash' }
    ]
  },
  {
    name: 'wallet',
    label: 'Wallet',
    type: 'checkboxList',
    field: 'wallet',
    options: [
      { label: 'Apple Wallet', value: 'Apple Wallet' },
      { label: 'Google Wallet', value: 'Google Wallet' },
      { label: 'Other Payment', value: 'Other Payment' }
    ]
  },
  {
    name: 'cardOnFile',
    label: 'Card on file used',
    type: 'dropdown',
    field: 'cof_used',
    options: [
      { label: 'Yes', value: '1' },
      { label: 'No', value: '0' }
    ]
  },
  {
    name: 'individualTickets',
    label: 'Individual tickets',
    type: 'dropdown',
    field: 'create_individual_passes',
    options: [
      { label: 'Yes', value: '1' },
      { label: 'No', value: '0' }
    ]
  },
  {
    name: 'newCustomer',
    label: 'New customer',
    type: 'dropdown',
    field: 'new_customer',
    options: [
      { label: 'Yes', value: '1' },
      { label: 'No', value: '0' }
    ]
  },
  {
    name: 'guestCustomer',
    label: 'Guest customer',
    type: 'dropdown',
    field: 'guest',
    options: [
      { label: 'Yes', value: '1' },
      { label: 'No', value: '0' }
    ]
  }
]

export const CUSTOMERS_LIST_FILTERS = [
  {
    name: 'status',
    label: 'Status',
    type: 'dropdown',
    field: 'active',
    options: [
      { label: 'Active', value: '1' },
      { label: 'Inactive', value: '0' }
    ]
  },
  {
    name: 'guest',
    label: 'Guest',
    type: 'dropdown',
    field: 'guest',
    options: [
      { label: 'Yes', value: '1' },
      { label: 'No', value: '0' }
    ]
  },
  { name: 'lastOrder', label: 'Last order', type: 'dateSelector', field: 'last_order_at', period: '' },
  { name: 'ordersCount', label: 'Order', type: 'numberRange', field: 'orders_count' },
  { name: 'lifetimeValue', label: 'Lifetime value', type: 'numberRange', field: 'lifetime_value' },
  { name: 'signInCount', label: 'Sign Ins', type: 'numberRange', field: 'sign_in_count' },
  { name: 'lastSignIn', label: 'Last Sign In', type: 'dateSelector', field: 'last_sign_in_at', period: '' },
  {
    name: 'hasNotes',
    label: 'Has notes',
    type: 'dropdown',
    field: 'has_notes',
    options: [
      { label: 'Yes', value: '1' },
      { label: 'No', value: '0' }
    ]
  },
  { name: 'createdAt', label: 'Created date', type: 'dateSelector', field: 'inserted_at' },
  { name: 'updatedAt', label: 'Updated date', type: 'dateSelector', field: 'updated_at' }
]

export const PASSES_LIST_FILTERS = [
  { name: 'scansCount', label: 'Scans', type: 'numberRange', field: 'scans_count' },
  { name: 'lastScannedAt', label: 'Last scanned', type: 'dateSelector', field: 'last_scanned_at', period: '' },
  { name: 'usesRemaining', label: 'Uses remaining', type: 'numberRange', field: 'use_balance_current' },
  { name: 'expiresAt', label: 'Expires', type: 'dateSelector', field: 'expires_at', period: '' },
  { name: 'voidedAt', label: 'Voided', type: 'dateSelector', field: 'voided_at', period: '' },
  { name: 'createdAt', label: 'Created date', type: 'dateSelector', field: 'inserted_at', period: '' },
  { name: 'updatedAt', label: 'Updated date', type: 'dateSelector', field: 'updated_at', period: '' }
]

export const VALIDATORS_LIST_FILTERS = [
  {
    name: 'status',
    label: 'Status',
    type: 'dropdown',
    field: 'active',
    options: [
      { label: 'Active', value: '1' },
      { label: 'Inactive', value: '0' }
    ]
  },
  {
    name: 'allowListSynced',
    label: 'Allow List Synced',
    type: 'dateSelector',
    field: 'allow_list_synced_at',
    period: ''
  },
  {
    name: 'allowListFullySynced',
    label: 'Allow List Fully Synced',
    type: 'dateSelector',
    field: 'allow_list_fully_synced_at',
    period: ''
  },
  {
    name: 'blockListSynced',
    label: 'Block List Synced',
    type: 'dateSelector',
    field: 'block_list_synced_at',
    period: ''
  },
  { name: 'scansSynced', label: 'Scans Synced', type: 'dateSelector', field: 'scans_synced_at', period: '' },
  {
    name: 'lastHeartbeat',
    label: 'Last heartbeat',
    type: 'dropdown',
    field: 'last_heartbeat_at',
    options: [
      { label: 'past hour', value: 'past_hour' },
      { label: 'between 1 and 12 hours', value: 'between_1_and_12_hours' },
      { label: 'between 13 and 24 hours', value: 'between_13_and_24_hours' },
      { label: 'more than 24 hours', value: 'more_than_24_hours' },
      { label: 'anytime', value: 'anytime' },
      { label: 'no heartbeats', value: 'no_heartbeats' }
    ]
  },
  { name: 'createdAt', label: 'Created date', type: 'dateSelector', field: 'inserted_at', period: '' },
  { name: 'updatedAt', label: 'Updated date', type: 'dateSelector', field: 'updated_at', period: '' }
]

export const QR_CODES_LIST_FILTERS = [
  {
    name: 'status',
    label: 'Status',
    type: 'dropdown',
    field: 'active',
    options: [
      { label: 'Active', value: '1' },
      { label: 'Inactive', value: '0' }
    ]
  },
  { name: 'views', label: 'Views', type: 'numberRange', field: 'impressions' },
  { name: 'createdAt', label: 'Created date', type: 'dateSelector', field: 'inserted_at' },
  { name: 'updatedAt', label: 'Updated date', type: 'dateSelector', field: 'updated_at' }
]

export const ROLE_LABELS: { [key in UserRole]: string } = {
  [UserRole.ACCESS_CONTROL]: 'Access control',
  [UserRole.ANALYST]: 'Analyst',
  [UserRole.SALES]: 'Sales',
  [UserRole.SUPERVISOR]: 'Supervisor',
  [UserRole.ADMIN]: 'Admin',
  [UserRole.SYSTEM]: 'System',
  [UserRole.ALL]: 'All'
}

export const ROLE_OPTIONS = [
  { label: 'Access control', value: UserRole.ACCESS_CONTROL },
  { label: 'Analyst', value: UserRole.ANALYST },
  { label: 'Sales', value: UserRole.SALES },
  { label: 'Supervisor', value: UserRole.SUPERVISOR },
  { label: 'Admin', value: UserRole.ADMIN }
]

export const USERS_LIST_FILTERS = [
  {
    name: 'status',
    label: 'Status',
    type: 'dropdown',
    field: 'active',
    options: [
      { label: 'Active', value: '1' },
      { label: 'Inactive', value: '0' }
    ]
  },
  {
    name: 'role',
    label: 'Role',
    type: 'checkboxList',
    field: 'role',
    options: ROLE_OPTIONS
  },
  { name: 'signInCount', label: 'Sign in count', type: 'numberRange', field: 'sign_in_count' },
  { name: 'lastSignIn', label: 'Last sign in', type: 'dateSelector', field: 'current_sign_in_at' },
  { name: 'createdAt', label: 'Created date', type: 'dateSelector', field: 'inserted_at' },
  { name: 'updatedAt', label: 'Updated date', type: 'dateSelector', field: 'updated_at' }
]

export const SIGN_OUT_NOTIFICATION_COOKIE_NAME = 'sign-out-notification'

export const VOIDED_REASONS = {
  mistake: 'Created by mistake',
  duplicate: 'Duplicate',
  fraud: 'Fraud',
  customer_request: 'Requested by customer'
}

export enum SERVER_ERRORS {
  BROWSER_MISMATCH = 'browser_mismatch',
  EXPIRED_LINK = 'expired_link',
  INACTIVE_USER = 'inactive_user',
  PASSWORD_RESET_REQUIRED = 'password_reset_required'
}

export const NOTE_KINDS = [
  { label: 'General', value: 'general' },
  { label: 'Customer complaint', value: 'customer_complaint' }
]

export const REFUND_TYPES = {
  FULL: 'full',
  PARTIAL: 'partial'
}

export const TAILWIND_BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
}

export const PASSKEY_ERRORS = {
  INVALID_STATE: 'InvalidStateError',
  NOT_ALLOWED: 'NotAllowedError'
}

export const PRINTER_SERVICE_ERROR_MESSAGES = {
  FAILED_TO_IDENTIFY_PRINTER: 'Failed to identify printer.',
  INVALID_IP_ADDRESS: 'Invalid IP address format.',
  INVALID_PORT: 'Must be between 1024 and 65535, inclusive.',
  IP_ADDRESS_ALREADY_ASSIGNED: 'That IP address is already assigned to {{name}} ({{referenceXid}}).',
  SERVICE_NOT_RUNNING: 'The Pronto print agent must be running to identify a printer.',
  NO_RESPONSE: 'No response received from Pronto print agent.',
  FAILED_TO_ADD_PRINTER: 'Failed to add printer.',
  INVALID_PRINTER_DATA: 'Something failed: One or more printer details are missing or invalid. Please try again.',
  MISSING_FIELDS: 'Identity printer validation failed: missing field(s) {{fields}}',
  SPECIAL_CHARACTERS: 'Identity printer validation failed: special characters in {{fields}}',
  BOTH_ERRORS:
    'Identity printer validation failed: missing fields in {{missingFields}}, special characters in {{invalidFields}}'
}

export const ORDER_ITEM_TYPES = ['Adult', 'Kid', 'Kid (0-4)', 'Kid (5-12)', 'Kid (0-12)', 'Bike']

// List obtained from https://github.com/prontocx/transit-validator-settings-lambda/blob/main/vessels.mjs
// This list should be fetched from an endpoint but for now is hard-coded
export const VESSELS_LIST = [
  {
    name: 'Commodore',
    code: 'COMM'
  },
  {
    name: 'Courageous',
    code: 'SSCG'
  },
  {
    name: "Martha's Vineyard Express",
    code: 'MVEX'
  },
  {
    name: 'Millennium',
    code: 'MILL'
  },
  {
    name: 'Nantucket Express',
    code: 'NEXP'
  },
  {
    name: 'Ocean State',
    code: 'SSOS'
  },
  {
    name: 'Seastreak Highlands',
    code: 'SSHG'
  },
  {
    name: 'Seastreak New Jersey',
    code: 'SSNJ'
  },
  {
    name: 'Seastreak New York',
    code: 'SSNY'
  },
  {
    name: 'Whaling City Express',
    code: 'WCEX'
  }
]

export const CARDS_LIST_FILTERS = [
  { name: 'added', label: 'Added', type: FilterType.DATE_SELECTOR, field: 'inserted_at' },
  { name: 'deactivated', label: 'Deactivated', type: FilterType.DATE_SELECTOR, field: 'inactive_at' },
  { name: 'assigned', label: 'Assigned', type: FilterType.DATE_SELECTOR, field: 'assigned_at' },
  {
    name: 'frozen',
    label: 'Frozen',
    type: FilterType.DROPDOWN,
    field: 'frozen',
    options: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' }
    ]
  },
  { name: 'replaced', label: 'Replaced', type: FilterType.DATE_SELECTOR, field: 'replaced_at' },
  { name: 'voided', label: 'Voided', type: FilterType.DATE_SELECTOR, field: 'voided_at' },
  { name: 'expires', label: 'Expires', type: FilterType.DATE_SELECTOR, field: 'expires_at' },
  { name: 'scans', label: 'Scans', type: FilterType.NUMBER_RANGE, field: 'scans_count' },
  { name: 'lastScanned', label: 'Last scanned', type: FilterType.DATE_SELECTOR, field: 'last_scanned_at' },
  { name: 'tripsRemaining', label: 'Trips remaining', type: FilterType.NUMBER_RANGE, field: 'use_balance_current' }
]

export const PRINTED_TICKETS_LIST_FILTERS = [
  { name: 'created', label: 'Created', type: FilterType.DATE_SELECTOR, field: 'inserted_at' },
  { name: 'voided', label: 'Voided', type: FilterType.DATE_SELECTOR, field: 'voided_at' },
  { name: 'expires', label: 'Expires', type: FilterType.DATE_SELECTOR, field: 'expires_at' },
  { name: 'scans', label: 'Scans', type: FilterType.NUMBER_RANGE, field: 'scans_count' },
  { name: 'lastScanned', label: 'Last scanned', type: FilterType.DATE_SELECTOR, field: 'last_scanned_at' },
  { name: 'usesRemaining', label: 'Uses remaining', type: FilterType.NUMBER_RANGE, field: 'uses_remaining' }
]

export const COUPONS_LIST_FILTERS = [
  {
    name: 'status',
    label: 'Status',
    type: FilterType.DROPDOWN,
    field: 'active',
    options: [
      { label: 'Active', value: 'true' },
      { label: 'Inactive', value: 'false' }
    ]
  },
  {
    name: 'type',
    label: 'Type',
    type: FilterType.CHECKBOX_LIST,
    field: 'type',
    options: COUPON_TYPE_OPTIONS
  },
  { name: 'usageCount', label: 'Usage Count', type: FilterType.NUMBER_RANGE, field: 'usage_count' },
  { name: 'startAt', label: 'Start Date', type: FilterType.DATE_SELECTOR, field: 'start_at' },
  { name: 'endAt', label: 'End Date', type: FilterType.DATE_SELECTOR, field: 'end_at' }
]

export const PRINT_RECEIPT_OPTIONS = [
  { label: 'No', value: 'no' },
  { label: 'Yes', value: 'yes' }
]

export const CUT_TICKETS_OPTIONS = [
  { label: 'After receipt', value: 'after_receipt' },
  { label: 'After all tickets', value: 'after_all_tickets' },
  { label: 'After each ticket', value: 'after_each_ticket' }
]

export const POS_FILTERS: ApiFilter[] = [
  {
    field: ProductSetting.AVAILABLE_IN_POS,
    value: 'true',
    op: '=='
  },
  {
    field: 'published',
    value: 'true',
    op: '=='
  },
  {
    field: 'price',
    value: '0',
    op: '>='
  },
  {
    field: 'archived',
    value: 'false',
    op: '=='
  }
]

export const enum LOCAL_STORAGE_KEYS {
  REDIRECT_TO = 'redirectTo',
  POS_ORDER_ID = 'dashboard-pos-order-id'
}

export const SIMULATOR_SETTINGS = {
  simulation_results: [
    {
      label: 'Approved (+0.00)',
      decimal_amount: '0.00'
    },
    {
      label: 'Declined, call issuer (+0.01)',
      decimal_amount: '0.01'
    },
    {
      label: 'Declined, offline pin required (+0.02)',
      decimal_amount: '0.02'
    },
    {
      label: 'Declined, online or offline pin required (+0.03)',
      decimal_amount: '0.03'
    },
    {
      label: 'Declined, generic (+0.05)',
      decimal_amount: '0.05'
    },
    {
      label: 'Declined, incorrect pin (+0.55)',
      decimal_amount: '0.55'
    },
    {
      label: 'Declined, withdrawal count limit exceeded (+0.65)',
      decimal_amount: '0.65'
    },
    {
      label: 'Declined, pin try exceeded (+0.75)',
      decimal_amount: '0.75'
    }
  ],
  test_cards: [
    {
      label: 'Visa *4242',
      payment_method: 'visa',
      card_number: '4242424242424242'
    },
    {
      label: 'Visa debit *5556',
      payment_method: 'visa_debit',
      card_number: '4000056655665556'
    },
    {
      label: 'Mastercard *4444',
      payment_method: 'mastercard',
      card_number: '5555555555554444'
    },
    {
      label: 'Mastercard debit *8210',
      payment_method: 'mastercard_debit',
      card_number: '5200828282828210'
    },
    {
      label: 'Mastercard prepaid *5100',
      payment_method: 'mastercard_prepaid',
      card_number: '5105105105105100'
    },
    {
      label: 'American Express *0005',
      payment_method: 'amex',
      card_number: '378282246310005'
    },
    {
      label: 'American Express *8431',
      payment_method: 'amex2',
      card_number: '371449635398431'
    },
    {
      label: 'Discover *1117',
      payment_method: 'discover',
      card_number: '6011111111111117'
    },
    {
      label: 'Discover *9424',
      payment_method: 'discover2',
      card_number: '6011000990139424'
    },
    {
      label: 'Diners Club *0004',
      payment_method: 'diners',
      card_number: '3056930009020004'
    },
    {
      label: 'Diners Club 14 digit *1667',
      payment_method: 'diners_14digits',
      card_number: '36227206271667'
    },
    {
      label: 'JCB *0505',
      payment_method: 'jcb',
      card_number: '3566002020360505'
    },
    {
      label: 'UnionPay *0005',
      payment_method: 'unionpay',
      card_number: '6200000000000005'
    },
    {
      label: 'Interac *1933',
      payment_method: 'interac',
      card_number: '4506445006931933'
    }
  ]
}

export const PRODUCT_OPTION_TYPE_OPTIONS = [
  { label: 'Single Select', value: ProductOptionType.SINGLE_SELECT },
  { label: 'Counter', value: ProductOptionType.COUNTER }
]

export const PRODUCT_OPTION_KEY_OPTIONS = [
  { label: 'Route', value: ProductOptionKeyType.ROUTE },
  { label: 'Fare', value: ProductOptionKeyType.FARE },
  { label: 'Passenger', value: ProductOptionKeyType.PASSENGER },
  { label: 'Other', value: ProductOptionKeyType.CUSTOM }
]

export const PRODUCT_OPTION_KEY_VALUES = {
  [ProductOptionKeyType.ROUTE]: [
    { label: 'Belford', value: 'Belford' },
    { label: 'Flex', value: 'Flex' },
    { label: 'Transfer', value: 'Transfer' }
  ],
  [ProductOptionKeyType.FARE]: [
    { label: 'Round trip', value: 'Round trip' },
    { label: 'One way', value: 'One way' }
  ],
  [ProductOptionKeyType.PASSENGER]: [
    { label: 'Adults', value: 'Adult' },
    { label: 'Seniors', value: 'Senior' },
    { label: 'Kids (0-4)', value: 'Under5' },
    { label: 'Kids (5-12)', value: 'Kid' },
    { label: 'Kids (0-12)', value: 'Kid0to12' },
    { label: 'Bikes', value: 'Bike' }
  ]
}

export enum SIGN_OUT_REASONS {
  POS_INACTIVITY = 'pos_role_inactive'
}

export const VALIDATOR_SYNC_LOGS_LIST_FILTERS = [
  {
    name: 'date',
    label: 'Date',
    type: 'dateSelector',
    field: 'inserted_at'
  },
  {
    name: 'event',
    label: 'Event',
    type: 'checkboxList',
    field: 'event',
    options: [
      { label: 'Locations', value: 'locations' },
      { label: 'Vessels', value: 'vessels' },
      { label: 'Full', value: 'full' },
      { label: 'Differential', value: 'differential' },
      { label: 'Block', value: 'block' },
      { label: 'Unknown', value: 'unknown' }
    ]
  },
  {
    name: 'statusCode',
    label: 'Status code',
    type: 'checkboxList',
    field: 'response_status_code',
    options: [
      { label: '200 - OK', value: 200 },
      { label: '400 - Bad Request', value: 400 },
      { label: '403 - Forbidden', value: 403 },
      { label: '404 - Not Found', value: 404 },
      { label: '406 - Not Acceptable', value: 406 },
      { label: '500 - Internal Server Error', value: 500 }
    ]
  }
]

export enum ReportType {
  FINANCIAL_CLOSE = 'financial_close',
  POS_OPERATOR_ACTIVITY = 'pos_operator_activity',
  POS_COMPLIMENTARY_ITEMS = 'pos_complimentary_items',
  EXPIRED_VALUE_BY_TICKET_TYPE_AND_PASSENGER_TYPE = 'expired_value_by_ticket_type_and_passenger_type',
  REDEMPTION_VALUE_BY_TICKET_TYPE_AND_PASSENGER_TYPE = 'redemption_value_by_ticket_type_and_passenger_type',
  REDEMPTION_VALUE_BY_DEPARTURE_PORT_AND_ARRIVAL_PORT = 'redemption_value_by_departure_port_and_arrival_port',
  ORDERS_BY_ROUTE = 'orders_by_route',
  ORDERS_BY_FARE = 'orders_by_fare',
  ORDERS_BY_TICKET_TYPE = 'orders_by_ticket_type',
  TICKETS_BY_TICKET_TYPE_AND_PASSENGER_TYPE = 'tickets_by_ticket_type_and_passenger_type',
  ORDERS_BY_TRIP_CARD_TOP_UP = 'orders_by_trip_card_top_up',
  PASSENGERS_PER_ORDER_BY_ROUTE = 'passengers_per_order_by_route',
  PASSENGERS_PER_ORDER_BY_FARE = 'passengers_per_order_by_fare',
  PASSENGERS_PER_ORDER_BY_TICKET_TYPE = 'passengers_per_order_by_ticket_type',
  SCANS_BY_ROUTE = 'scans_by_route',
  SCANS_BY_VESSEL = 'scans_by_vessel',
  PAYMENTS_BY_PAYMENT_NETWORK = 'payments_by_payment_network',
  PAYMENTS_BY_PAYMENT_CARD_FUNDING_TYPE = 'payments_by_payment_card_funding_type',
  PAYMENTS_BY_MOBILE_WALLET_TYPE = 'payments_by_mobile_wallet_type'
}
